import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Button,
  Typography,
  useTheme,
  Paper,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  Avatar,
  Tooltip,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FunctionsIcon from '@mui/icons-material/Functions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DevicesIcon from '@mui/icons-material/Devices';
import PersonIcon from '@mui/icons-material/Person';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CodeIcon from '@mui/icons-material/Code';

import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import CollectionActions from './components/CollectionActions';
import SelectField from '../common/components/SelectField';
import { useAdministrator } from '../common/util/permissions';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { useEffectAsync, useCatch } from '../reactHelper';
import useCrudExports from '../common/hooks/useCrudExports';
import usePositionAttributes from '../common/attributes/usePositionAttributes';
import { formatBoolean } from '../common/util/formatter';
import { snackBarDurationLongMs } from '../common/util/duration';

const ComputedAttributesPage = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const theme = useTheme();
  const administrator = useAdministrator();
  const positionAttributes = usePositionAttributes(t);

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Estados para teste
  const [deviceId, setDeviceId] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [result, setResult] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // Fetch de dados
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetchOrThrow('/api/attributes/computed');
      setItems(await response.json());
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  // Estatísticas
  const stats = useMemo(() => ({
    total: items.length,
    active: items.filter((i) => !i.disabled).length,
    inactive: items.filter((i) => i.disabled).length,
    string: items.filter((i) => i.type === 'string').length,
    number: items.filter((i) => i.type === 'number').length,
  }), [items]);

  const statsCards = [
    {
      key: 'total',
      label: t('attributeTotal'),
      value: stats.total,
      color: theme.palette.primary.main,
      Icon: FunctionsIcon,
    },
    {
      key: 'active',
      label: t('attributeActive'),
      value: stats.active,
      color: theme.palette.success.main,
      Icon: CheckCircleIcon,
    },
    {
      key: 'string',
      label: t('attributeTypeString'),
      value: stats.string,
      color: theme.palette.info.main,
      Icon: DevicesIcon,
    },
    {
      key: 'number',
      label: t('attributeTypeNumber'),
      value: stats.number,
      color: theme.palette.warning.main,
      Icon: PersonIcon,
    },
  ];

  // Filtros
  const filteredItems = useMemo(() => {
    let result = items;
    if (statusFilter === 'active') result = result.filter((i) => !i.disabled);
    else if (statusFilter === 'inactive') result = result.filter((i) => i.disabled);
    if (typeFilter) result = result.filter((i) => i.type === typeFilter);
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (i) =>
          i.description?.toLowerCase().includes(lower) ||
          i.attribute?.toLowerCase().includes(lower) ||
          i.expression?.toLowerCase().includes(lower) ||
          i.id?.toString().includes(lower)
      );
    }
    return result;
  }, [items, statusFilter, typeFilter, searchText]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  // Exportação
  const exportColumns = [
    { field: 'id', headerNameKey: 'sharedId' },
    { field: 'description', headerNameKey: 'sharedDescription' },
    { field: 'attribute', headerNameKey: 'sharedAttribute' },
    { field: 'expression', headerNameKey: 'attributeExpression' },
    { field: 'type', headerNameKey: 'sharedType' },
    {
      field: 'disabled',
      headerNameKey: 'sharedDisabled',
      valueGetter: ({ row }) => formatBoolean(row.disabled, t),
    },
  ];

  const { handleExportCsv, handleExportXlsx, handleExportPdf } = useCrudExports({
    titleKey: 'reportAttributesTitle',
    headerKey: 'reportAttributesHeader',
    rows: filteredItems,
    columns: exportColumns,
    theme,
    t,
    options: { includeWatermark: true },
  });

  // Teste do atributo (adaptado do ComputedAttributePage.jsx)
  const testAttribute = useCatch(async () => {
    if (!selectedItem || !deviceId) return;
    const query = new URLSearchParams({ deviceId });
    const url = `/api/attributes/computed/test?${query.toString()}`;
    const response = await fetchOrThrow(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(selectedItem),
    });
    const text = await response.text();
    setResult(text);
    setOpenSnackbar(true);
  });

  const handleTestAttribute = (id) => {
    const item = items.find((i) => i.id === id);
    setSelectedItem(item);
    setOpenDialog(true);
  };

  const formatAttributeName = (attributeKey) => {
    if (!attributeKey) return '';
    if (positionAttributes && positionAttributes[attributeKey]?.name) {
      return positionAttributes[attributeKey].name;
    }
    return attributeKey.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
  };

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'sharedComputedAttributes']}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* CARDS DE ESTATÍSTICAS */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            {statsCards.map((card) => (
              <Card key={card.key} elevation={1} sx={{ flex: '1 1 200px', p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: card.color + '20' }}>
                  <card.Icon sx={{ color: card.color, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">{card.label}</Typography>
                  <Typography variant="h6" fontWeight="bold">{card.value}</Typography>
                </Box>
              </Card>
            ))}
          </Box>

          {/* BARRA DE AÇÕES */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight="bold">{t('sharedComputedAttributes')}</Typography>
            <TextField
              size="small"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
              placeholder={t('sharedSearch')}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="primary" /></InputAdornment> }}
              sx={{ flex: 1 }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="info"
                size="small"
                startIcon={<AddCircleIcon />}
                onClick={() => navigate('/settings/attribute')}
                disabled={!administrator}
              >
                {t('sharedAdd')}
              </Button>
              {administrator && (
                <>
                  <Button variant="contained" color="csv" size="small" startIcon={<FileDownloadIcon />} onClick={handleExportCsv}>CSV</Button>
                  <Button variant="contained" color="excel" size="small" startIcon={<TableChartIcon />} onClick={handleExportXlsx}>Excel</Button>
                  <Button variant="contained" color="pdf" size="small" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf}>PDF</Button>
                </>
              )}
            </Stack>
          </Stack>

          {/* TABELA */}
          <Paper sx={{ flex: 1, overflow: 'hidden', borderRadius: 2 }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>ID</TableCell>
                    <TableCell>{t('sharedDescription')}</TableCell>
                    <TableCell>{t('sharedAttribute')}</TableCell>
                    <TableCell>{t('attributeExpression')}</TableCell>
                    <TableCell>{t('sharedType')}</TableCell>
                    <TableCell>Status</TableCell>
                    {administrator && <TableCell>{t('sharedActions')}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} align="center"><CircularProgress /></TableCell></TableRow>
                  ) : paginated.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center"><Typography variant="body2" color="text.secondary">{t('sharedNoData')}</Typography></TableCell></TableRow>
                  ) : (
                    paginated.map((row) => (
                      <TableRow hover key={row.id}>
                        <TableCell><Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}><FunctionsIcon fontSize="small" /></Avatar></TableCell>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.description || '-'}</TableCell>
                        <TableCell><Chip label={formatAttributeName(row.attribute)} size="small" /></TableCell>
                        <TableCell>
                          <Tooltip title={row.expression || '-'}>
                            <Chip
                              icon={<CodeIcon fontSize="small" />}
                              label={row.expression?.slice(0, 30) + (row.expression?.length > 30 ? '...' : '')}
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell><Chip label={row.type || '-'} color={row.type === 'string' ? 'info' : 'warning'} size="small" /></TableCell>
                        <TableCell><Chip label={row.disabled ? t('sharedDisabled') : t('deviceStatusActive')} color={row.disabled ? 'error' : 'success'} size="small" /></TableCell>
                        {administrator && (
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title={t('attributeTest')}>
                                <IconButton size="small" onClick={() => handleTestAttribute(row.id)} sx={{ color: theme.palette.success.main }}>
                                  <PlayArrowIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <CollectionActions
                                itemId={row.id}
                                editPath="/settings/attribute"
                                endpoint="attributes/computed"
                                setTimestamp={setTimestamp}
                              />
                            </Stack>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredItems.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        </Box>
      </Box>

      {/* DIALOG TESTE */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlayArrowIcon sx={{ color: theme.palette.success.main }} />
          {t('attributeTest')}
        </DialogTitle>

        <DialogContent dividers>
          <SelectField
            endpoint="/api/devices"
            label={t('sharedDevice')}
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            size="small"
            fullWidth
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
              setTimeout(() => document.activeElement.blur(), 50);
              setDeviceId('');
              setSelectedItem(null);
            }}
          >
            {t('sharedCancel')}
          </Button>

          <Button
            onClick={async () => {
              await testAttribute();
              setOpenDialog(false);
              setTimeout(() => document.activeElement.blur(), 50);
              setDeviceId('');
              setSelectedItem(null);
            }}
            variant="contained"
            color="success"
            disabled={!deviceId}
          >
            {t('sharedTest')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR RESULTADO */}
      <Snackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        autoHideDuration={snackBarDurationLongMs}
        message={result}
      />
    </PageLayout>
  );
};

export default ComputedAttributesPage;
